import { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from '../components/Toast';
import { useWishlistContext } from '../context/WishlistContext';
import { CartContext } from '../context/CartContext';
import EmptyState from '../components/EmptyState';
import { API_URL } from '../config';

export default function Wishlist() {
    const navigate = useNavigate();
    const { wishlist, wishlistLoading, removeFromWishlist, moveToCart } = useWishlistContext();
    const { addToCart, refreshCart } = useContext(CartContext);

    const [actionLoading, setActionLoading] = useState({});

    useEffect(() => {
        document.title = 'My Wishlist | Fone Express';
    }, []);

    // ── Helpers ───────────────────────────────────────────────────────────────

    /** Build a full image URL from variant image data */
    const getImageUrl = (item) => {
        // item.variant.image holds the relative path populated by the server
        const path = item.variant?.image;
        if (!path) return '/placeholder.png';
        if (path.startsWith('http')) return path;
        return `${API_URL}/${path}`;
    };

    /** Build a display name with variant specs */
    const getDisplayName = (item) => {
        const baseName = item.product?.name || 'Product';
        const specs = [];
        if (item.variant?.variantAttributes?.length) {
            item.variant.variantAttributes.forEach((a) => {
                const valName = a.valueId?.name || a.value;
                const attrName = a.attributeId?.name || a.name;
                if (valName && attrName) specs.push(`${valName} ${attrName}`);
            });
        }
        if (item.variant?.colorId?.name) specs.push(item.variant.colorId.name);
        if (specs.length > 0) return `${baseName} (${specs.join(', ')})`;
        return baseName;
    };

    // ── Actions ──────────────────────────────────────────────────────────────

    const handleRemove = async (item) => {
        const vId = item.variantId || item.variant?._id;
        if (!vId) return;
        setActionLoading((prev) => ({ ...prev, [vId]: 'remove' }));
        try {
            await removeFromWishlist(vId);
            toast.success('Removed from wishlist');
        } catch (err) {
            console.error('Error removing from wishlist:', err);
            toast.error(err?.response?.data?.message || 'Failed to remove item');
        } finally {
            setActionLoading((prev) => ({ ...prev, [vId]: null }));
        }
    };

    const handleMoveToCart = async (item) => {
        const vId = item.variantId || item.variant?._id;
        if (!vId) return;
        setActionLoading((prev) => ({ ...prev, [vId]: 'cart' }));
        try {
            await moveToCart(vId);
            toast.success('Moved to cart');
        } catch (err) {
            console.error('Error moving to cart:', err);
            toast.error(err?.response?.data?.message || 'Failed to move item to cart');
        } finally {
            setActionLoading((prev) => ({ ...prev, [vId]: null }));
        }
    };

    const handleCardClick = (item) => {
        const slug = item.product?.slug;
        if (slug) navigate(`/product/${slug}`);
    };

    // ── Loading skeleton (matches ProductCard grid) ──────────────────────────

    if (wishlistLoading) {
        return (
            <div className="py-12">
                <div className="max-w-[1400px] mx-auto px-4">
                    <div className="mb-8">
                        <h1 className="text-2xl md:text-3xl font-bold text-foreground">My Wishlist</h1>
                        <p className="text-muted-foreground text-sm mt-1">Loading your wishlist...</p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                        {[...Array(8)].map((_, i) => (
                            <div
                                key={i}
                                className="bg-card rounded-lg shadow-sm border border-border overflow-hidden animate-pulse"
                            >
                                <div className="p-4 bg-muted">
                                    <div className="w-full h-48 bg-muted rounded"></div>
                                </div>
                                <div className="p-4 space-y-3">
                                    <div className="h-4 bg-muted rounded w-3/4"></div>
                                    <div className="h-4 bg-muted rounded w-1/2"></div>
                                    <div className="h-5 bg-muted rounded w-1/3"></div>
                                    <div className="h-10 bg-muted rounded w-full"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // ── UI ────────────────────────────────────────────────────────────────────

    return (
        <div className="py-12">
            <div className="max-w-[1400px] mx-auto px-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-foreground">My Wishlist</h1>
                        <p className="text-muted-foreground text-sm mt-1">
                            {wishlist.length > 0
                                ? `${wishlist.length} item${wishlist.length !== 1 ? 's' : ''} saved`
                                : 'No items saved yet'}
                        </p>
                    </div>
                    {wishlist.length > 0 && (
                        <Link
                            to="/products"
                            className="text-primary hover:text-primary/85 font-medium flex items-center gap-1 transition-colors whitespace-nowrap group"
                        >
                            Continue Shopping
                            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                        </Link>
                    )}
                </div>

                {/* Empty State */}
                {wishlist.length === 0 && (
                    <EmptyState
                        icon="ri-heart-line"
                        title="Your wishlist is empty"
                        description="Save items you love to your wishlist. Review them anytime and easily move them to the cart."
                        actionLabel="Browse Products"
                        actionLink="/products"
                    />
                )}

                {/* Wishlist Grid — same card layout as home page ProductCard */}
                {wishlist.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                        {wishlist.map((item) => {
                            const vId = item.variantId || item.variant?._id;
                            const isActioning = actionLoading[vId];
                            const slug = item.product?.slug;
                            const productLink = slug ? `/product/${slug}` : '#';
                            const imageUrl = getImageUrl(item);
                            const displayName = getDisplayName(item);

                            // Pricing
                            const salePrice = item.pricing?.price || 0;
                            const discountedPrice = item.pricing?.discountedPrice || salePrice;
                            const hasOffer = item.pricing?.offer && discountedPrice < salePrice;
                            const discount = hasOffer
                                ? Math.round(((salePrice - discountedPrice) / salePrice) * 100)
                                : 0;

                            const inStock = item.inStock !== false && item.status !== 'out_of_stock' && item.status !== 'unavailable';

                            return (
                                <div
                                    key={item._id}
                                    className="bg-card rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-border flex flex-col h-full group relative"
                                    onClick={() => handleCardClick(item)}
                                >
                                    {/* Image Section */}
                                    <div className="relative p-4 bg-muted rounded-t-lg">
                                        <img
                                            src={imageUrl}
                                            alt={displayName}
                                            className="w-full h-48 object-contain"
                                        />

                                        {/* Discount Badge */}
                                        {discount > 0 && (
                                            <div className="absolute top-2 left-2 bg-primary text-white px-2 py-1 rounded text-xs font-semibold">
                                                {discount}% OFF
                                            </div>
                                        )}

                                        {/* Remove from Wishlist Button (replaces the heart btn) */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemove(item);
                                            }}
                                            disabled={isActioning === 'remove'}
                                            className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-card rounded-full shadow-sm hover:shadow-md hover:bg-red-50 transition-all disabled:opacity-60"
                                            title="Remove from wishlist"
                                        >
                                            {isActioning === 'remove' ? (
                                                <i className="ri-loader-4-line animate-spin text-red-500 text-sm"></i>
                                            ) : (
                                                <i className="ri-heart-fill text-primary"></i>
                                            )}
                                        </button>

                                        {/* Out-of-stock overlay */}
                                        {!inStock && (
                                            <div className="absolute inset-0 bg-card/60 flex items-center justify-center rounded-t-lg">
                                                <span className="bg-red-500 text-white text-xs font-semibold px-3 py-1 rounded">
                                                    Out of Stock
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Product Info */}
                                    <div className="p-4 flex flex-col flex-1">
                                        <h3 className="text-sm font-medium text-foreground mb-2 line-clamp-2 min-h-[40px]">
                                            {displayName}
                                        </h3>

                                        {/* Brand */}
                                        {item.product?.brand?.name && (
                                            <p className="text-xs text-muted-foreground mb-2">{item.product.brand.name}</p>
                                        )}

                                        {/* Pricing */}
                                        <div className="mb-3">
                                            <div className="flex items-baseline gap-2 mb-1">
                                                <span className="text-xl font-bold text-foreground">
                                                    KSh {(hasOffer ? discountedPrice : salePrice).toLocaleString()}
                                                </span>
                                                {hasOffer && (
                                                    <span className="text-sm text-muted-foreground line-through">
                                                        KSh {salePrice.toLocaleString()}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Offer tag */}
                                            {item.pricing?.offer?.name && (
                                                <p className="text-xs text-success">
                                                    {item.pricing.offer.name}
                                                </p>
                                            )}
                                        </div>

                                        {/* Move to Cart Button */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleMoveToCart(item);
                                            }}
                                            disabled={!inStock || isActioning === 'cart'}
                                            className="w-full bg-primary hover:brightness-110 hover:shadow-lg text-white py-2.5 rounded-lg font-medium transition-all duration-300 mt-auto whitespace-nowrap opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                            {isActioning === 'cart' ? (
                                                <>
                                                    <i className="ri-loader-4-line animate-spin"></i>
                                                    Moving...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ri-shopping-cart-line"></i>
                                                    Move to Cart
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
